/* import __COLOCATED_TEMPLATE__ from './cx-score-section.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type AppService from 'embercom/services/app-service';
import type CustomReportsService from 'embercom/services/custom-reports-service';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type { PercentageMetricValue } from 'embercom/lib/reporting/chart-data-resource';
import type { ParticipatedFilter } from 'embercom/components/reporting/bespoke/fin-performance/types';

interface Signature {
  Args: {
    reportState: ReportState;
    participatedFilter?: ParticipatedFilter;
  };
}

export default class CxScoreSection extends Component<Signature> {
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare customReportsService: CustomReportsService;

  @tracked showBenchmarkPanel = false;

  @use cxScoreDataResource = staticDataResource(this, {
    serializedChart: () => this.cxScoreChartDefinition,
    reportState: () => this.args.reportState,
  });

  @action
  openBenchmarkPanel() {
    this.showBenchmarkPanel = true;
  }

  @action
  closeBenchmarkPanel() {
    this.showBenchmarkPanel = false;
  }

  get hasCxScore(): boolean {
    return this.appCxScoreForBenchmark !== undefined;
  }

  get appCxScoreForBenchmark(): number | undefined {
    if (this.cxScoreDataResource.isLoading) {
      return undefined;
    }
    let kpiData = this.cxScoreDataResource.kpiData;
    if (!kpiData || !kpiData[0]) {
      return undefined;
    }
    let percentageValue = (kpiData[0] as PercentageMetricValue).value;
    if (percentageValue === null || percentageValue === undefined) {
      return undefined;
    }
    // ES metric returns CX score as a percentage (0-100), but the benchmark
    // panel expects a decimal (0-1) for display and percentile calculations.
    return percentageValue / 100;
  }

  get cxScoreObjects() {
    return {
      key_5: {
        label: '5',
        barColorClass: 'bg-vis-violet-20',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-5',
        ),
      },
      key_4: {
        label: '4',
        barColorClass: 'bg-vis-violet-40',
        gapAfter: true,
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-4',
        ),
      },
      key_3: {
        label: '3',
        barColorClass: 'bg-vis-orange-70',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-3',
        ),
      },
      key_2: {
        label: '2',
        barColorClass: 'bg-vis-orange-50',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-2',
        ),
      },
      key_1: {
        label: '1',
        barColorClass: 'bg-vis-orange-30',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-score-1',
        ),
      },
    };
  }

  get positiveObjects() {
    return {
      'Positive product feedback': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-product-feedback',
        ),
        barColorClass: 'bg-vis-violet-10',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-positive-product-feedback',
        ),
      },
      'High Fin answer quality': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-fin-answer-quality',
        ),
        barColorClass: 'bg-vis-violet-30',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-high-fin-answer-quality',
        ),
      },
      'Positive user emotion': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-user-emotion',
        ),
        barColorClass: 'bg-vis-violet-50',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-positive-user-emotion',
        ),
      },
      'Positive policy feedback': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-policy-feedback',
        ),
        barColorClass: 'bg-vis-violet-70',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-positive-policy-feedback',
        ),
      },
      'Low user effort': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-user-effort',
        ),
        barColorClass: 'bg-vis-violet-80',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-low-user-effort',
        ),
      },
    };
  }

  get finParticipatedFilter() {
    if (this.args.participatedFilter) {
      return this.args.participatedFilter;
    }
    return {
      type: 'category',
      data: {
        property: 'agent_types_involved_grouping',
        attribute: 'conversation.agent_types_involved_grouping',
        values: ['ai-agent'],
      },
    };
  }

  get negativeObjects() {
    return {
      'Negative product feedback': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-product-feedback',
        ),
        barColorClass: 'bg-vis-orange-10',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-negative-product-feedback',
        ),
      },
      'Low Fin answer quality': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-fin-answer-quality',
        ),
        barColorClass: 'bg-vis-orange-30',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-low-fin-answer-quality',
        ),
      },
      'Negative user emotion': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-user-emotion',
        ),
        barColorClass: 'bg-vis-orange-50',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-negative-user-emotion',
        ),
      },
      'Negative policy feedback': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-policy-feedback',
        ),
        barColorClass: 'bg-vis-orange-70',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-negative-policy-feedback',
        ),
      },
      'High user effort': {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.label-user-effort',
        ),
        barColorClass: 'bg-vis-orange-80',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.cx-score-section.tooltip-high-user-effort',
        ),
      },
    };
  }

  get cxScoreChartDefinition() {
    return {
      visualization_type: 'counter',
      chart_series: [
        {
          metric_id:
            'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
          filters: {
            type: 'and',
            filters: [this.finParticipatedFilter],
          },
        },
      ],
    };
  }

  get cxScoreBreakdownChartDefinition() {
    return {
      visualization_type: 'horizontal-bar',
      view_by: 'ai_generated_metrics.csat',
      stacked: true,
      chart_series: [
        {
          metric_id:
            'conversation.ai_generated_metrics.csat.count.first_user_conversation_part_created_at',
          aggregation: 'count',
          show_data_labels: false,
          filters: {
            type: 'and',
            filters: [this.finParticipatedFilter],
          },
        },
      ],
    };
  }
  get positiveCxScoreReasonsChartDefinition() {
    return {
      visualization_type: 'horizontal-bar',
      view_by: 'conversation_custom_fields#customer_experience_attributes',
      chart_series: [
        {
          metric_id: 'conversation.ai_generated_metrics.csat.count',
          aggregation: 'count',
          show_data_labels: false,
          filters: {
            type: 'and',
            filters: [
              this.finParticipatedFilter,
              {
                type: 'category',
                data: {
                  property: 'conversation_custom_fields#customer_experience_attributes',
                  attribute: 'conversation_custom_fields#customer_experience_attributes',
                  values: [
                    'product_feedback:positive',
                    'policy_feedback:positive',
                    'user_strong_emotion:positive',
                    'user_effort:low',
                    'answer_quality_bot:high',
                  ],
                },
              },
              {
                type: 'category',
                data: {
                  property: 'ai_generated_metrics.csat',
                  attribute: 'conversation.ai_generated_metrics.csat',
                  values: ['4', '5'],
                },
              },
            ],
          },
        },
      ],
    };
  }
  get negativeCxScoreReasonsChartDefinition() {
    return {
      visualization_type: 'horizontal-bar',
      view_by: 'conversation_custom_fields#customer_experience_attributes',
      chart_series: [
        {
          metric_id: 'conversation.ai_generated_metrics.csat.count',
          aggregation: 'count',
          show_data_labels: false,
          filters: {
            type: 'and',
            filters: [
              this.finParticipatedFilter,
              {
                type: 'category',
                data: {
                  property: 'ai_generated_metrics.csat',
                  attribute: 'conversation.ai_generated_metrics.csat',
                  values: ['1', '2'],
                },
              },
              {
                type: 'category',
                data: {
                  property: 'conversation_custom_fields#customer_experience_attributes',
                  attribute: 'conversation_custom_fields#customer_experience_attributes',
                  values: [
                    'product_feedback:negative',
                    'policy_feedback:negative',
                    'user_strong_emotion:negative',
                    'user_effort:high',
                    'answer_quality_bot:low',
                  ],
                },
              },
            ],
          },
        },
      ],
    };
  }

  @action
  openLearnMoreArticle() {
    safeWindowOpen(
      'https://www.intercom.com/help/en/articles/10495092-understand-customer-experience-at-scale-with-the-cx-score',
      '_blank',
    );
  }

  @action
  redirectToCxScoreReport() {
    if (this.appService.app.isStandaloneApp) {
      safeWindowOpen(this.router.urlFor('apps.app.standalone.reports.custom-report'), '_blank');
    } else {
      safeWindowOpen(
        this.router.urlFor('apps.app.reports.views.view', 'my-reports', {
          queryParams: {
            showTemplateId: 'ai_csat',
          },
        }),
        '_blank',
      );
    }
  }

  get shouldHideReportButton() {
    // standalone apps don't have a CX report, so we hide the button
    // we also hide the button if the teammate doesn't have permission to change custom reports
    // as they need that to be able to see the report templates
    return (
      this.appService.app.canUseStandalone || !this.customReportsService.canChangeCustomReports
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::CxScoreSection': typeof CxScoreSection;
    'reporting/bespoke/fin-performance/cx-score-section': typeof CxScoreSection;
  }
}
